import { bump, motionOk } from "./kit";
import { portraitUrl } from "./portraits";

/**
 * DOM boss bar (art wow pass Phase 2, "Boss arrival and fall"): a portrait disc, the boss's name on a red plate and a
 * 16 px health bar with a notch at the 50 % phase change, docked under the HUD plate on the left (the map-rotate
 * buttons keep the right). It replaces reading a boss's health off a world-space bar at phone zoom.
 *
 * Driven by the units session's boss events (PHASE2_BRIEF "Boss events"): spawn slides it in, hp moves the fill (and a
 * white chip that trails losses, as on the HUD), phase 2 flashes it and marks it ENRAGED, death drains it and lets it
 * go. Transform/opacity only; the fill and chip slide by translateX inside a clipped track.
 */
export class BossBar {
  readonly root: HTMLDivElement;
  private readonly fill: HTMLElement;
  private readonly chip: HTMLElement;
  private shown = 1;
  private hideTimer = 0;
  /** After the death drain, how long the bar stays before it fades (lets the fall land first). */
  static readonly LingerMs = 900;

  constructor(parent: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "bossbar hidden";
    this.root.innerHTML = `<div class="bb-face"><img alt=""></div><div class="bb-body"><div class="bb-name plate disp"><span></span><em class="bb-tag">ENRAGED</em></div><div class="bb-track"><div class="bb-chip"></div><div class="bb-fill"></div><i class="bb-notch"></i></div></div>`;
    this.fill = this.root.querySelector(".bb-fill")!;
    this.chip = this.root.querySelector(".bb-chip")!;
    parent.append(this.root);
  }

  /** The boss rises: portrait, name, a full bar, slid in from above. */
  spawn(archId: string, name: string): void {
    window.clearTimeout(this.hideTimer);
    const url = portraitUrl(archId);
    const img = this.root.querySelector<HTMLImageElement>(".bb-face img")!;
    if (url) { img.src = url; img.classList.remove("hidden"); } else img.classList.add("hidden");
    this.root.querySelector(".bb-name span")!.textContent = name;
    this.root.classList.remove("hidden", "enraged", "dead", "leaving");
    this.shown = 1;
    this.set(1, true);
    if (motionOk()) this.root.animate([{ transform: "translateY(-24px)", opacity: 0 }, { transform: "none", opacity: 1 }], { duration: 380, easing: "cubic-bezier(.2,1.3,.4,1)" });
  }

  /** Health moved (0..1). A loss pops the portrait a little; the chip drains after it. */
  hp(frac: number): void {
    if (this.root.classList.contains("hidden")) return;
    const f = Math.max(0, Math.min(1, frac));
    const lost = f < this.shown - 1e-4;
    this.set(f, !lost);
    if (lost && this.shown - f > 0.02) bump(this.root.querySelector(".bb-face"), 1.1, 160);
    this.shown = f;
  }

  /** Phase change (n ≥ 2): the bar flashes white and turns molten; the ENRAGED tag drops in. */
  phase(n: number): void {
    if (n < 2 || this.root.classList.contains("hidden")) return;
    this.root.classList.add("enraged");
    if (motionOk()) {
      this.root.querySelector<HTMLElement>(".bb-track")!.animate([{ opacity: 1 }, { opacity: 0.35 }, { opacity: 1 }, { opacity: 0.5 }, { opacity: 1 }], { duration: 420 });
      bump(this.root, 1.08, 260);
    }
  }

  /** The boss falls: the bar empties, the plate greys, then it all fades away. */
  death(): void {
    if (this.root.classList.contains("hidden")) return;
    this.set(0, false);
    this.root.classList.add("dead");
    window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => this.leave(), BossBar.LingerMs);
  }

  /** The boss walked into the fortress (no fall): the bar fades now, without the death drain. */
  gone(): void {
    if (this.root.classList.contains("hidden")) return;
    window.clearTimeout(this.hideTimer);
    this.leave();
  }

  /**
   * Self-heal, called as the views sync (level/levelSync.ts): a bar up with no boss alive in the sim for HealMs goes,
   * whatever event was missed — a portrait and a red plate that no tap can clear must never outstay its boss (M30 #3).
   * The grace covers a killed boss whose round is still flying (its death, and the drain, come when it lands).
   */
  heal(bossAlive: boolean, now = performance.now()): void {
    if (bossAlive || this.root.classList.contains("hidden")) { this.orphanSince = -1; return; }
    if (this.orphanSince < 0) { this.orphanSince = now; return; }
    if (now - this.orphanSince >= BossBar.HealMs) { this.orphanSince = -1; this.hide(); }
  }
  private orphanSince = -1;
  /** How long a bar may stay up with no boss alive before it is taken down (ms). */
  static readonly HealMs = 4000;

  private leave(): void {
    if (!motionOk() || typeof this.root.animate !== "function") { this.root.classList.add("hidden"); return; }
    this.root.animate([{ transform: "none", opacity: 1 }, { transform: "translateY(-12px) scale(.94)", opacity: 0 }], { duration: 320, easing: "ease-in", fill: "forwards" })
      .onfinish = (e) => { this.root.classList.add("hidden"); (e.target as Animation).cancel(); };
  }

  /** The level is over: the bar goes at once, mid-drain or not — it must not sit on the end screen or the chest. */
  hide(): void {
    window.clearTimeout(this.hideTimer);
    for (const a of this.root.getAnimations?.() ?? []) a.cancel();
    this.root.classList.add("hidden");
  }

  private set(f: number, instantChip: boolean): void {
    const t = `translateX(${((f - 1) * 100).toFixed(2)}%)`;
    this.fill.style.transform = t;
    this.chip.classList.toggle("instant", instantChip);
    this.chip.style.transform = t;
    this.root.classList.toggle("low", f <= 0.25 && f > 0);
  }

  dispose(): void { window.clearTimeout(this.hideTimer); this.root.remove(); }
}

/** What `attachBossBar` needs from the enemies view — structural, so it works with either event shape (see below). */
type Listeners = Record<string, unknown>;

/**
 * Subscribes the bar to the enemies view's boss events. The units session's Phase 2 view has listener arrays —
 * `onBossSpawn(boss)`, `onBossHp(frac, boss)`, `onBossPhase(n, boss)`, `onBossDeath(boss, worldPos)` — and the Phase 1
 * view had single nullable callbacks (`onBossHp(id, hp01, archId)`). This accepts both, chaining rather than replacing
 * an existing single callback, so it is correct before and after the units branch merges.
 */
export function attachBossBar(enemies: object, bar: BossBar, nameOf: (archId: string) => string): void {
  const e = enemies as Listeners;
  const archOf = (b: unknown): string => (b && typeof b === "object" && "archId" in b ? String((b as { archId: unknown }).archId) : "");
  const on = (key: string, arrayFn: (...a: unknown[]) => void, singleFn: (...a: unknown[]) => void): void => {
    const cur = e[key];
    if (Array.isArray(cur)) { cur.push(arrayFn); return; }
    if (cur === null || typeof cur === "function" || key in e) {
      const prev = typeof cur === "function" ? (cur as (...a: unknown[]) => void) : null;
      e[key] = (...a: unknown[]) => { prev?.(...a); singleFn(...a); };
    }
  };
  on("onBossSpawn", (b) => bar.spawn(archOf(b), nameOf(archOf(b))), (b) => bar.spawn(archOf(b), nameOf(archOf(b))));
  on("onBossHp", (frac) => bar.hp(Number(frac)), (_id, hp01) => bar.hp(Number(hp01)));
  on("onBossPhase", (n) => bar.phase(Number(n)), (n) => bar.phase(Number(n)));
  on("onBossDeath", () => bar.death(), () => bar.death());
  if (Array.isArray(e.onBossGone)) (e.onBossGone as unknown[]).push(() => bar.gone());
}
